import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../@/components/ui/table";
import { Checkbox } from "../../@/components/ui/checkbox";
import { EmptyState } from "./emptyState";
import { BookOpenIcon, Trash2, X } from "lucide-react";

export default function DefaultTable({ 
  columns, 
  data, 
  emptyMessage = "No results.",
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  bulkActions = [],
  onBulkAction
}) {
  const isSelectionEnabled = Boolean(onSelectItem || onSelectAll);
  const hasMoreOptionColumn = useMemo(() => {
    const keys = new Set(["actions", "action", "more", "options", "menu"]);
    return Array.isArray(columns)
      ? columns.some((col) => keys.has(String(col?.key || "")))
      : false;
  }, [columns]);

  // Only show the selector column when the table has a "more/actions" column.
  const shouldShowSelector = hasMoreOptionColumn && isSelectionEnabled;

  const getRowKey = (row, index) =>
    row?.row_id ?? row?.rowKey ?? row?.id ?? row?.session_id ?? index;

  const rowKeys = useMemo(
    () => (Array.isArray(data) ? data.map((row, i) => getRowKey(row, i)) : []),
    [data]
  );
  const selectedKeySet = useMemo(() => new Set(selectedItems || []), [selectedItems]);

  const isAllSelected = rowKeys.length > 0 && rowKeys.every((k) => selectedKeySet.has(k));
  const isSomeSelected = rowKeys.some((k) => selectedKeySet.has(k)) && !isAllSelected;

  const selectedCountOnPage = useMemo(
    () => rowKeys.filter((k) => selectedKeySet.has(k)).length,
    [rowKeys, selectedKeySet]
  );

  const selectedRowsOnPage = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.filter((row, i) => selectedKeySet.has(getRowKey(row, i)));
  }, [data, selectedKeySet]);

  const shouldShowBulkActionsOverlay =
    shouldShowSelector &&
    selectedCountOnPage >= 2 &&
    Array.isArray(bulkActions) &&
    bulkActions.length > 0;

  const visibleColumns = useMemo(() => {
    const cols = Array.isArray(columns) ? columns : [];
    const withoutCheckbox = cols.filter((col) => String(col?.key || "") !== "checkbox");

    if (!shouldShowSelector) return withoutCheckbox;

    const hasCheckbox = cols.some((col) => String(col?.key || "") === "checkbox");
    return hasCheckbox ? cols : [{ key: "checkbox", header: null }, ...cols];
  }, [columns, shouldShowSelector]);

  const handleToggleAll = (nextChecked) => {
    const checked = Boolean(nextChecked);

    if (onSelectAll) {
      // Backwards compatible: callers that only accept (checked) will ignore extra args.
      onSelectAll(checked, rowKeys, data);
      return;
    }

    // Fallback when callers only provided onSelectItem (toggle behavior).
    if (!onSelectItem) return;

    if (checked) {
      rowKeys.forEach((key) => {
        if (!selectedKeySet.has(key)) onSelectItem(key);
      });
      return;
    }

    rowKeys.forEach((key) => {
      if (selectedKeySet.has(key)) onSelectItem(key);
    });
  };

  const clearSelection = () => {
    if (onSelectAll) {
      onSelectAll(false, rowKeys, data);
      return;
    }

    if (!onSelectItem) return;
    rowKeys.forEach((key) => {
      if (selectedKeySet.has(key)) onSelectItem(key);
    });
  };

  const selectedCountLabel = `${selectedCountOnPage} item${selectedCountOnPage === 1 ? "" : "s"} selected`;
  
  return (
    <div className={shouldShowBulkActionsOverlay ? "relative" : "relative"}>
      <Table className="text-(--neutral-900) bg-(--surface-base) p-0 border border-(--neutral-300) rounded-md">
        <TableHeader className="bg-(--neutral-100)">
          <TableRow className="border-b border-(--neutral-300)">
            {visibleColumns.map((col) => (
              <TableHead
                key={col.key}
                className={`h-10 overflow-visible font-heading text-12px font-semibold text-(--neutral-700) ${col.key === "checkbox" ? "w-10" : ""}`}
              >
                {col.key === "checkbox" ? (
                  <div className="flex items-center justify-center">
                    <Checkbox
                      checked={
                        isAllSelected || (isSomeSelected && "indeterminate")
                      }
                      onCheckedChange={handleToggleAll}
                      aria-label="Select all rows"
                    />
                  </div>
                ) : (
                  col.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length ? (
            data.map((row, i) => {
              // Prefer a stable unique key when callers provide it.
              // Using only `id` can be non-unique in list views (e.g. one user
              // can have multiple enrolments), which causes React to duplicate/omit rows.
              const rowKey = getRowKey(row, i);
              return (
                <TableRow
                  key={rowKey}
                  className="border-b last:border-b-0 border-(--neutral-300) bg-(--surface-base)"
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={`align-top font-body text-14px font-normal text-(--neutral-800) ${col.key === "checkbox" ? "w-10" : ""}`}
                    >
                      {col.key === "checkbox" ? (
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedKeySet.has(rowKey)}
                            onCheckedChange={() =>
                              onSelectItem && onSelectItem(rowKey)
                            }
                            aria-label={`Select row ${rowKey}`}
                          />
                        </div>
                      ) : col.render ? (
                          col.render(row, row[col.key])
                        ) : (
                          row[col.key] ?? "—"
                        )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell 
                 colSpan={visibleColumns.length} 
                  className="bg-(--neutral-100) p-0"
              >
                <div className="bg-(--neutral-100)">
                  <EmptyState icon={BookOpenIcon} description={emptyMessage} />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {shouldShowBulkActionsOverlay ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center h-10">
          <div className="pointer-events-auto flex items-center overflow-hidden rounded-md border border-(--neutral-300) bg-(--surface-base)">
            <div className="px-3 py-2 font-body text-14px text-(--neutral-700)">
              {selectedCountLabel}
            </div>

            {bulkActions.map((label) => {
              const text = String(label ?? "");
              const normalized = text.trim().toLowerCase();
              const isDelete = normalized.includes("delete");

              return (
                <button
                  key={text}
                  type="button"
                  className={`border-l border-(--neutral-300) px-3 py-2 font-body text-14px hover:bg-(--neutral-100) ${
                    isDelete ? "text-(--color-destructive)" : "text-(--neutral-900)"
                  }`}
                  onClick={() =>
                    onBulkAction?.(text, {
                      selectedRowKeysOnPage: rowKeys.filter((k) => selectedKeySet.has(k)),
                      selectedCountOnPage,
                      selectedRowsOnPage,
                      rowsOnPage: data,
                    })
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    {isDelete ? <Trash2 className="h-4 w-4" /> : null}
                    {text}
                  </span>
                </button>
              );
            })}

            <button
              type="button"
              aria-label="Clear selection"
              className="border-l border-(--neutral-300) px-3 py-2 text-(--neutral-700) hover:bg-(--neutral-100)"
              onClick={clearSelection}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}